import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import { useSEO } from '@/hooks/useSEO';

export default function AboutPage() {
  const { t } = useTranslation();

  const reviews = t('about.reviews', { returnObjects: true }) as Array<{
    name: string;
    avatar: string;
    rating: number;
    comment: string;
  }>;

  useSEO({
    title: '关于我们 - 漫画之家_韩国免费漫画平台',
    description: '漫画之家是领先的韩国免费漫画在线阅读平台，提供海量下拉式韩漫大全。了解我们的品牌故事、用户口碑和联系方式。',
    keywords: '漫画之家,关于我们,韩国漫画平台,免费韩漫',
    canonicalUrl: 'https://www.manhuazhijia.com/about',
    schemas: [
      {
        '@context': 'https://schema.org',
        '@type': 'AboutPage',
        name: '关于漫画之家',
        description: '漫画之家是领先的韩国免费漫画在线阅读平台',
        url: 'https://www.manhuazhijia.com/about',
        isPartOf: {
          '@type': 'WebSite',
          name: '漫画之家',
          url: 'https://www.manhuazhijia.com',
        },
      },
      {
        '@context': 'https://schema.org',
        '@type': 'Organization',
        name: '漫画之家',
        url: 'https://www.manhuazhijia.com',
        description: '在漫画之家，您能免费阅读下拉式韩漫大全，在线浏览各种韩国漫画，体验流畅的阅读服务。',
        address: {
          '@type': 'PostalAddress',
          streetAddress: '张江高科技园区漫画之家大厦1001室',
          addressLocality: '上海',
          addressRegion: '浦东新区',
          addressCountry: 'CN',
        },
        contactPoint: [
          {
            '@type': 'ContactPoint',
            email: 'contact@manhuazhijia.com',
            telephone: '+86 400-888-6666',
            contactType: 'customer service',
            availableLanguage: ['Chinese'],
          },
        ],
        sameAs: [
          'https://www.manhuazhijia.com',
        ],
      },
    ],
  });

  return (
    <div className="min-h-screen bg-background-50">
      <ScrollToTop />
      <Navbar />

      <main className="pt-16">
        <div className="bg-white border-b border-background-200/70">
          <div className="max-w-[1400px] mx-auto px-4 md:px-6">
            <Breadcrumb
              items={[
                { label: t('breadcrumb.home'), href: '/' },
                { label: t('breadcrumb.about') },
              ]}
            />
          </div>
        </div>

        <section className="max-w-[1200px] mx-auto px-4 md:px-6 py-10">
          <div className="text-center mb-12">
            <h1 className="text-3xl md:text-4xl font-bold text-foreground-900 mb-3">
              {t('about.title')}
            </h1>
            <p className="text-sm text-foreground-500 max-w-lg mx-auto">
              致力于为您提供最优质的韩国漫画阅读体验
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-14">
            <div className="bg-white rounded-lg p-8">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center">
                  <i className="ri-building-2-line text-lg text-primary-500"></i>
                </div>
                <h2 className="text-xl font-bold text-foreground-900">
                  {t('about.brandTitle')}
                </h2>
              </div>
              <p className="text-sm text-foreground-600 leading-relaxed">
                {t('about.brandDesc')}
              </p>
            </div>

            <div className="bg-white rounded-lg p-8">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-accent-100 flex items-center justify-center">
                  <i className="ri-flag-2-line text-lg text-accent-500"></i>
                </div>
                <h2 className="text-xl font-bold text-foreground-900">
                  {t('about.missionTitle')}
                </h2>
              </div>
              <p className="text-sm text-foreground-600 leading-relaxed">
                {t('about.missionDesc')}
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-14">
            <div className="bg-white rounded-lg p-6 text-center">
              <div className="w-14 h-14 mx-auto mb-3 rounded-full bg-primary-100 flex items-center justify-center">
                <i className="ri-book-open-line text-2xl text-primary-500"></i>
              </div>
              <h3 className="text-lg font-bold text-foreground-900 mb-1">1000+</h3>
              <p className="text-sm text-foreground-500">海量韩漫资源</p>
            </div>
            <div className="bg-white rounded-lg p-6 text-center">
              <div className="w-14 h-14 mx-auto mb-3 rounded-full bg-accent-100 flex items-center justify-center">
                <i className="ri-user-heart-line text-2xl text-accent-500"></i>
              </div>
              <h3 className="text-lg font-bold text-foreground-900 mb-1">500万+</h3>
              <p className="text-sm text-foreground-500">活跃读者用户</p>
            </div>
            <div className="bg-white rounded-lg p-6 text-center">
              <div className="w-14 h-14 mx-auto mb-3 rounded-full bg-secondary-100 flex items-center justify-center">
                <i className="ri-thumb-up-line text-2xl text-secondary-500"></i>
              </div>
              <h3 className="text-lg font-bold text-foreground-900 mb-1">99.8%</h3>
              <p className="text-sm text-foreground-500">用户好评率</p>
            </div>
          </div>

          <div className="mb-14">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-foreground-900 mb-2">
                {t('about.repoTitle')}
              </h2>
              <p className="text-sm text-foreground-500">{t('about.repoDesc')}</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {Array.isArray(reviews) && reviews.map((review, index) => (
                <div key={index} className="bg-white rounded-lg p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0">
                      <img
                        src={review.avatar}
                        alt={review.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-foreground-900">{review.name}</h3>
                      <div className="flex items-center gap-0.5">
                        {Array.from({ length: review.rating }).map((_, i) => (
                          <i key={i} className="ri-star-fill text-amber-400 text-xs"></i>
                        ))}
                      </div>
                    </div>
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    &ldquo;{review.comment}&rdquo;
                  </p>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white rounded-lg p-8">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-foreground-900 mb-2">
                {t('about.contactTitle')}
              </h2>
              <p className="text-sm text-foreground-500">{t('about.contactDesc')}</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="text-center p-4">
                <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-primary-100 flex items-center justify-center">
                  <i className="ri-mail-line text-xl text-primary-500"></i>
                </div>
                <h3 className="text-sm font-semibold text-foreground-900 mb-1">
                  {t('about.email')}
                </h3>
                <a href="mailto:contact@manhuazhijia.com" className="text-sm text-primary-500 hover:text-primary-600">
                  {t('about.emailValue')}
                </a>
              </div>
              <div className="text-center p-4">
                <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-accent-100 flex items-center justify-center">
                  <i className="ri-phone-line text-xl text-accent-500"></i>
                </div>
                <h3 className="text-sm font-semibold text-foreground-900 mb-1">
                  {t('about.phone')}
                </h3>
                <a href="tel:+864008886666" className="text-sm text-accent-500 hover:text-accent-600">
                  {t('about.phoneValue')}
                </a>
              </div>
              <div className="text-center p-4">
                <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-secondary-100 flex items-center justify-center">
                  <i className="ri-map-pin-line text-xl text-secondary-500"></i>
                </div>
                <h3 className="text-sm font-semibold text-foreground-900 mb-1">
                  {t('about.address')}
                </h3>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  {t('about.addressValue')}
                </p>
              </div>
              <div className="text-center p-4">
                <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-primary-100 flex items-center justify-center">
                  <i className="ri-time-line text-xl text-primary-500"></i>
                </div>
                <h3 className="text-sm font-semibold text-foreground-900 mb-1">
                  {t('about.workingHours')}
                </h3>
                <p className="text-sm text-foreground-600">
                  {t('about.workingHoursValue')}
                </p>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}